import { create } from 'zustand';
import {
    generateShowMediaItemRequest,
    generateGetStatesRequest,
    generateStartSessionRequest,
    generateEndSessionRequest,
    AveMessageType,
} from '@org-avp/ave-message-factory';
import type {
    AveRequest,
    AveResponse,
    AveError,
} from '@org-avp/ave-message-factory';
import { WebrtcPlayer } from '@org-avp/webrtc-player';
import { Logger, createBrowserConsoleTransport } from '@org-avp/logger';
import Config, { ENV } from '../../js/config/_global';
import { Stage, AwsSecretSchema } from '@org-avp/models';
import type { AwsSecret } from '@org-avp/models';
import Server from '../../js/config/_server';
import { apiGet } from '../../js/api';
import { COOKIE_KEYS, ENV_TYPES } from '../../js/_constants';
import helpers from '../../../src/js/helpers';
import { setWelcomeScreen, endWelcomeScreen } from '@org-avp/2d-message-factory';
import { getAppStage } from './miwiStore';

const appStageToAveStageMap = {
  [ENV_TYPES.DEV]: 'develop',
  [ENV_TYPES.INT]: 'preprod',
  [ENV_TYPES.PROD]: 'prod',
};

const logger = new Logger({
  application: {
    app: 'RCM',
    version: Config.version,
    stage: appStageToAveStageMap[ENV] as Stage,
  },
  initiator: 'avpdevdfe',
  transports: [createBrowserConsoleTransport()],
  logLevel: 'debug',
});

const getLogger = () => logger;

const log = getLogger();

const _handleAveMessage = (msg: AveResponse<unknown>[]) => {
  const messageIsError = (message: AveResponse<unknown>): message is AveError =>
    message.meta.type === AveMessageType.ERROR;
  log.debug(`Received a message from the AVE - ${msg?.[0].meta.name}`, {
    msg: msg.length === 1 ? msg[0] : msg,
  });
  msg.filter(messageIsError).forEach(message => {
    log.warn('Received an AVE error', { error: message });
  });
};

const _handleDisconnect = () => {
  log.debug('WebrtcPlayer was disconnected from AVE');
};

let player = new WebrtcPlayer(log);

player.on('disconnected', _handleDisconnect);
player.on('aveMessage', _handleAveMessage);

player.sendCirrusRequest({
    type: "streamingConfig" as any,
    streamingConfig: { inactivityTimeoutTimeInSeconds: 30 * 60 }
  })

const sendMessage = (request: AveRequest<unknown>): void => {
  if (!player.isConnected()) {
    throw new Error('Not connected');
  }
  player.sendAveRequest({ ...request });
};

const fetchCredentialsFromTvm = async (
  overrideDealerId: string
): Promise<AwsSecret> => {
  const url =
    Server.baseUrl + Server.services.miwiAuthentication + overrideDealerId;
  const response = await apiGet(url);
  const credentials = AwsSecretSchema.safeParse(response);
  if (!credentials.success) {
    throw new Error('Malformed backend data', {
      cause: { error: credentials.error, response },
    });
  }
  return credentials.data;
};

interface WelcomeScreenStoreState {
    connectWelcomeScreenAVE: (id: string, dealerId: string) => Promise<void>;
    dissonnectWelcomeScreenAVE: () => Promise<void>;
    setWelcomeScreenAVE: (type: string, url: string, header?: string, message?: string, textColor?: { r: number, g: number, b: number, a: number }) => Promise<void>;
    setWelcomeScreenChrombox: (requestPayload: any) => Promise<void>;
    endWelcomeScreenChrombox: (deviceNumber: string) => Promise<void>;
  }

const useWelcomeScreenStore = create<WelcomeScreenStoreState>((set, get) => ({
    setWelcomeScreenChrombox: async (requestPayload:{ deviceNumber: string, url: string, customerName: string, message: string, template: '1' | '2' | '3' }) => {
      await setWelcomeScreen(getAppStage() as any, helpers.getCookie(COOKIE_KEYS.TOKEN), requestPayload);
    },
    endWelcomeScreenChrombox: async (deviceNumber: string) => {
      await endWelcomeScreen(getAppStage() as any, helpers.getCookie(COOKIE_KEYS.TOKEN), deviceNumber);
    },
    setWelcomeScreenAVE: async (type, url, header, message, textColor) => {
        sendMessage(generateShowMediaItemRequest({ type: type as any, url, header, message, textColor }));
    },
    connectWelcomeScreenAVE: async (id: string, dealerId: string) => {
      let timeoutId: NodeJS.Timeout | null = null;
      player = new WebrtcPlayer(log);
      try {
        const connectionPromise = player.connect({
          dataChannelOnly: true,
          htmlContainerId: 'ave-stream',
          signalingOptions: {
            type: 'aws',
            credentialsProvider: () => fetchCredentialsFromTvm(dealerId),
            channelName: `${dealerId}_${id}`,
            clientId: `${dealerId}_${id}-welcome-screen`,
          },
        });

        const timeoutPromise = new Promise<void>((_, reject) => {
          timeoutId = setTimeout(() => reject(new Error('Connection to AVE timed out')), 10000);
        });

        await Promise.race([connectionPromise, timeoutPromise]);
        
        if (timeoutId) clearTimeout(timeoutId);
  
        sendMessage(
          generateStartSessionRequest({
            client: `RCM;${Config.version};${ENV}`,
            sessionId: `${dealerId}-${id}-welcome-screen`,
          })
        );
  
        sendMessage(generateGetStatesRequest());
      } catch (error: unknown) {
        if (timeoutId) clearTimeout(timeoutId);
        log.error('Error connecting to private AVE', { id, error });
        throw new Error('Error connecting to private AVE');
      }
    },
    dissonnectWelcomeScreenAVE: async () => {
        log.debug('Disconnecting from AVE');
        if (player.isConnected()) {
            log.debug('Sending EndSession request to private AVE');
            sendMessage(generateEndSessionRequest());
            await player.disconnect();
        }
    },
}));

export default useWelcomeScreenStore;